// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { formatPrice } from '@codex/helpers';
import { Rating } from '@codex/basics';

const StyledSuggestionsPopup = styled.div`
  left: 3px;
  right: 3px;
  top: calc(100% - 1px);
  position: absolute;
  z-index: 98;
  overflow: hidden;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.colors.shades[5]};
  border-bottom: none;
  background: ${props => props.theme.colors.shades[7]};
  box-shadow: 0 3px 3px 0 rgba(102, 102, 102, 0.2);
  white-space: normal;
`;

const SuggestionLink = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid ${props => props.theme.colors.shades[5]};
  color: ${props => props.theme.colors.shades[1]};
  background-color: ${props => props.theme.colors.shades[7]};
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;

  &.active {
    background-color: ${props => props.theme.colors.message.hover};
  }
`;

const SuggestionButton = styled.button`
    display: block;
    border: none;
    border-bottom: 1px solid ${props => props.theme.colors.shades[5]};
    color: ${props => props.theme.colors.shades[1]};
    padding: 10px 20px;
    margin: 0;
    width: 100%;
    background-color: ${props => props.theme.colors.shades[7]};
    cursor: pointer;
    text-align: left;
    font-weight: bold;
    font-size: 14px;
    &.active {
      background-color: ${props => props.theme.colors.message.hover};
    }
`;

const ProductImage = styled.img`
  flex-shrink: 0;
  margin-right: ${({ theme }) => theme.spacing[4]};
  width: 45px;
  height: 40px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  min-height: 40px;
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const ProductTitle = styled.div`
  overflow: hidden;
  margin-right: ${({ theme }) => theme.spacing[4]};
  line-height: 15px;
  font-weight: normal;
  font-size: 13px;
`;

const ProductPrice = styled.div`
  color: ${({ theme }) => theme.colors.price.default};
  font-weight: bold;
  font-size: 14px;
`;

const ProductStock = styled.div`
  color: ${props => (props.unavailable
    ? props.theme.colors.header.suggestions.stock.unavailable
    : props.theme.colors.header.suggestions.stock.default)};
  font-weight: normal;
  font-size: 13px;
`;

const RatingStockContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const SuggestionsPopup = (
  {
    suggestions, highlightedSuggestionIndex, currency, onSuggestionItemMouseEnter,
    onSuggestionItemClick, productInStockMessage, productUnavailableMessage
  }
) => (
  <StyledSuggestionsPopup>
    {suggestions.map((item, i) => (
      item.type === 'sayt'
        ? (
          <SuggestionLink
            key={i}
            className={i === highlightedSuggestionIndex && 'active'}
            onMouseEnter={() => {
              onSuggestionItemMouseEnter(i);
            }}
            onMouseDown={e => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              onSuggestionItemClick();
            }}
          >
            <ProductImage alt="" src={item.image} />
            <ContentContainer>
              <TitleContainer>
                <ProductTitle>
                  {item.brand} {item.title}
                </ProductTitle>
                <ProductPrice>
                  {formatPrice(item.price, false, currency)}
                </ProductPrice>
              </TitleContainer>
              <RatingStockContainer>
                {(() => {
                  if (!item.hasOwnProperty('stock_quantity')) {
                    return;
                  }
                  if (item.stock_quantity > 0) {
                    return (
                      <ProductStock>
                        {productInStockMessage}
                      </ProductStock>
                    );
                  }
                  return (
                    <ProductStock unavailable>
                      {productUnavailableMessage}
                    </ProductStock>
                  );

                })()}
                {item.review_score && (
                  <Rating maxCount={5} ratingValue={parseInt(item.review_score, 10)} />
                )}
              </RatingStockContainer>
            </ContentContainer>
          </SuggestionLink>
        )
        : (
          <SuggestionButton
            key={i}
            className={i === highlightedSuggestionIndex && 'active'}
            onMouseEnter={() => {
              onSuggestionItemMouseEnter(i);
            }}
            onMouseDown={e => e.preventDefault()}
            onClick={(e) => {
              onSuggestionItemClick();
            }}
            type="submit"
          >
            {item.title}
          </SuggestionButton>
        )
    ))}
  </StyledSuggestionsPopup>
);

SuggestionsPopup.propTypes = {
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['sayt', 'term']).isRequired,
      url: PropTypes.string,
      image: PropTypes.string,
      brand: PropTypes.string,
      price: PropTypes.string,
      review_score: PropTypes.string,
      stock_quantity: PropTypes.int
    })
  ).isRequired,
  highlightedSuggestionIndex: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  onSuggestionItemMouseEnter: PropTypes.func.isRequired,
  onSuggestionItemClick: PropTypes.func.isRequired,
  productInStockMessage: PropTypes.string,
  productUnavailableMessage: PropTypes.string
};
SuggestionsPopup.defaultProps = {
  productInStockMessage: 'In stock near you',
  productUnavailableMessage: 'Not in stock near you'
};
export default SuggestionsPopup;
